import React, { useEffect, useRef, useState, useMemo } from "react";
import { Handle, Position, useReactFlow, useNodeId, useUpdateNodeInternals, NodeResizeControl } from "@xyflow/react";
import "./Node.css";
import GroupNodeLabel from "./GroupNodeLabel";
import MaterialIcon from "../../BuilderComponents/MaterialIcon";

const COMPACT_DIMENSIONS = { width: 120, height: 120 };
const MIN_WIDTH = 100;
const MIN_HEIGHT = 50;
const CHILD_PADDING = 80;

const GroupNode = ({ id, data, selected }) => {
  const [dimensions, setDimensions] = useState({ width: 120, height: 120 });
  const [hasChildren, setHasChildren] = useState(false);
  const [hovered, setHovered] = useState(false);
  const nodeRef = useRef(null);
  const reactFlowInstance = useReactFlow();
  const nodeId = useNodeId();
  const updateNodeInternals = useUpdateNodeInternals();

  const updateChildrenStatus = () => {
    const allNodes = reactFlowInstance.getNodes();
    const childNodes = allNodes.filter((node) => node.parentId === id);
    const hasChildrenNow = childNodes.length > 0;
    setHasChildren(hasChildrenNow);
    return { childNodes, hasChildrenNow };
  };

  const calculateChildBounds = (childNodes) => {
    if (childNodes.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    childNodes.forEach((child) => {
      const childWidth = child.width || child.measured?.width || MIN_WIDTH;
      const childHeight = child.height || child.measured?.height || MIN_HEIGHT;

      minX = Math.min(minX, child.position.x);
      minY = Math.min(minY, child.position.y);
      maxX = Math.max(maxX, child.position.x + childWidth);
      maxY = Math.max(maxY, child.position.y + childHeight);
    });

    return {
      width: maxX - minX + CHILD_PADDING * 2,
      height: maxY - minY + CHILD_PADDING * 2,
      minX,
      minY
    };
  };

  const fitToChildren = () => {
    const { childNodes } = updateChildrenStatus();
    const bounds = calculateChildBounds(childNodes);

    if (bounds) {
      setDimensions({
        width: Math.max(bounds.width, MIN_WIDTH),
        height: Math.max(bounds.height, MIN_HEIGHT)
      });

      // Adjust child positions relative to new parent size
      const updatedNodes = reactFlowInstance.getNodes().map((node) => {
        if (node.parentId !== id) return node;

        return {
          ...node,
          position: {
            x: node.position.x - bounds.minX + CHILD_PADDING,
            y: node.position.y - bounds.minY + CHILD_PADDING
          }
        };
      });

      reactFlowInstance.setNodes(updatedNodes);
    } else {
      setDimensions(COMPACT_DIMENSIONS);
    }
  };

  const alignChildrenVertically = () => {
    const { childNodes } = updateChildrenStatus();

    if (childNodes.length === 0) return;

    const verticalSpacing = 40;
    const verticalPadding = 40;

    // Calculate total height needed
    let totalHeight = verticalPadding * 2;
    childNodes.forEach((child) => {
      const childHeight = child.measured?.height || child.height || MIN_HEIGHT;
      totalHeight += childHeight + verticalSpacing;
    });
    totalHeight -= verticalSpacing;

    // Calculate required width based on widest child
    let maxChildWidth = MIN_WIDTH;
    childNodes.forEach((child) => {
      const childWidth = child.measured?.width || child.width || MIN_WIDTH;
      maxChildWidth = Math.max(maxChildWidth, childWidth);
    });

    const newWidth = Math.max(maxChildWidth + CHILD_PADDING * 2, MIN_WIDTH);

    setDimensions({
      width: newWidth,
      height: totalHeight
    });

    // Calculate positions using new dimensions
    const centerX = newWidth / 2;
    let currentY = verticalPadding;

    // Update child node positions
    const updatedNodes = reactFlowInstance.getNodes().map((node) => {
      if (node.parentId !== id) return node;

      const childHeight = node.measured?.height || node.height || MIN_HEIGHT;
      const childX = centerX - (node.measured?.width || node.width || MIN_WIDTH) / 2;

      const newNode = {
        ...node,
        position: {
          x: childX,
          y: currentY
        }
      };

      currentY += childHeight + verticalSpacing;
      return newNode;
    });

    reactFlowInstance.setNodes(updatedNodes);
    updateNodeInternals(id);

    if (data.updateParentPosition) {
      data.updateParentPosition();
    }
  };

  useEffect(() => {
    const { hasChildrenNow } = updateChildrenStatus();

    if (hasChildrenNow && !data.isAutoChildAlignMentRequired) {
      setTimeout(() => fitToChildren(), 400);
    }
    //eslint-disable-next-line
  }, [reactFlowInstance, id, data.positionParent]);

  useEffect(() => {
    const { hasChildrenNow } = updateChildrenStatus();
    if (hasChildrenNow && !data.isAutoChildAlignMentRequired) {
      setTimeout(() => alignChildrenVertically(), 1000);
    }
    //eslint-disable-next-line
  }, [reactFlowInstance, id, data.positionParent]);

  useEffect(() => {
    const { childNodes } = updateChildrenStatus();

    const { width, height } = data.positionParent || {};

    if (width > 120 && childNodes.length) {
      setDimensions({ height, width });
      return;
    }

    if (childNodes.length > 0) {
      const bounds = calculateChildBounds(childNodes);
      if (bounds) {
        setDimensions({
          width: Math.max(bounds.width, MIN_WIDTH),
          height: Math.max(bounds.height, MIN_HEIGHT)
        });
      }
    } else {
      setDimensions(COMPACT_DIMENSIONS);
    }
    //eslint-disable-next-line
  }, [reactFlowInstance, id, data.positionParent]);

  const getBorderStyle = useMemo(() => {
    if (selected) return `2px solid #3b82f6`;
    if (hovered) return `1px solid #3b82f6`;
    return `1px solid #94a3b8`;
  }, [selected, hovered]);

  const nodeStyle = useMemo(
    () => ({
      position: "relative",
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      border: getBorderStyle,
      background: "#f5f7f7",
      borderRadius: hasChildren ? "8px" : "50%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease"
    }),
    [dimensions.width, dimensions.height, getBorderStyle, hasChildren]
  );

  const AddButton = ({ size = "normal" }) => {
    const isSmall = size === "small";

    const buttonStyle = {
      background: "white",
      border: "1px solid #e2e8f0",
      borderRadius: isSmall ? "4px" : "50%",
      width: isSmall ? "50px" : "48px",
      height: isSmall ? "50px" : "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 0,
      boxShadow: isSmall ? "0 1px 2px rgba(0,0,0,0.05)" : "0 2px 4px rgba(0,0,0,0.1)"
    };

    return (
      <button onClick={() => data.onAddClick(data.type)} style={buttonStyle} title={`Add ${data.label}`}>
        <MaterialIcon
          name="add"
          style={{
            fontSize: "24px",
            color: isSmall ? "#64748b" : "#3b82f6"
          }}
        />
      </button>
    );
  };

  const handleStyle = {
    opacity: hovered ? 1 : 0,
    transition: "opacity 0.2s ease"
  };

  // Create a separate component for rectangular handles with more handles per edge
  const RectangularHandles = () => {
    return (
      <>
        <Handle
          type="source"
          position={Position.Top}
          id={`${nodeId}-source-0`}
          style={{ ...handleStyle, left: "50%", top: "0%" }}
        />

        <Handle
          type="target"
          position={Position.Top}
          id={`${nodeId}-target`}
          style={{ ...handleStyle, left: "50%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.TopRight}
          id={`${nodeId}-source-1`}
          style={{ ...handleStyle, left: "85%", top: "0%" }}
        />

        <Handle
          type="target"
          position={Position.TopRight}
          id={`${nodeId}-target-1`}
          style={{ ...handleStyle, left: "85%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.Right}
          id={`${nodeId}-source-2`}
          style={{ ...handleStyle, top: "25%" }}
        />
        <Handle
          type="target"
          position={Position.Right}
          id={`${nodeId}-target-2`}
          style={{ ...handleStyle, top: "25%" }}
        />

        <Handle
          type="source"
          position={Position.Right}
          id={`${nodeId}-source-3`}
          style={{ ...handleStyle, top: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Right}
          id={`${nodeId}-target-3`}
          style={{ ...handleStyle, top: "50%" }}
        />

        <Handle
          type="source"
          position={Position.Right}
          id={`${nodeId}-source-4`}
          style={{ ...handleStyle, top: "75%" }}
        />
        <Handle
          type="target"
          position={Position.Right}
          id={`${nodeId}-target-4`}
          style={{ ...handleStyle, top: "75%" }}
        />

        <Handle
          type="source"
          position={Position.BottomRight}
          id={`${nodeId}-source-5`}
          style={{ ...handleStyle, left: "85%", top: "100%" }}
        />
        <Handle
          type="target"
          position={Position.BottomRight}
          id={`${nodeId}-target-5`}
          style={{ ...handleStyle, left: "85%", top: "100%" }}
        />

        <Handle
          type="source"
          position={Position.Bottom}
          id={`${nodeId}-source`}
          style={{ ...handleStyle, left: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id={`${nodeId}-target-6`}
          style={{ ...handleStyle, left: "50%" }}
        />

        <Handle
          type="source"
          position={Position.BottomLeft}
          id={`${nodeId}-source-7`}
          style={{ ...handleStyle, left: "15%", top: "100%" }}
        />
        <Handle
          type="target"
          position={Position.BottomLeft}
          id={`${nodeId}-target-7`}
          style={{ ...handleStyle, left: "15%", top: "100%" }}
        />

        <Handle
          type="source"
          position={Position.Left}
          id={`${nodeId}-source-8`}
          style={{ ...handleStyle, left: "0%", top: "25%" }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id={`${nodeId}-target-8`}
          style={{ ...handleStyle, left: "0%", top: "25%" }}
        />

        <Handle
          type="source"
          position={Position.Left}
          id={`${nodeId}-source-9`}
          style={{ ...handleStyle, left: "0%", top: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id={`${nodeId}-target-9`}
          style={{ ...handleStyle, left: "0%", top: "50%" }}
        />

        <Handle
          type="source"
          position={Position.Left}
          id={`${nodeId}-source-10`}
          style={{ ...handleStyle, left: "0%", top: "75%" }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id={`${nodeId}-target-10`}
          style={{ ...handleStyle, left: "0%", top: "75%" }}
        />

        <Handle
          type="source"
          position={Position.TopLeft}
          id={`${nodeId}-source-11`}
          style={{ ...handleStyle, left: "15%", top: "0%" }}
        />
        <Handle
          type="target"
          position={Position.TopLeft}
          id={`${nodeId}-target-11`}
          style={{ ...handleStyle, left: "15%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.Top}
          id={`${nodeId}-source-12`}
          style={{ ...handleStyle, left: "30%", top: "0%" }}
        />
        <Handle
          type="target"
          position={Position.Top}
          id={`${nodeId}-target-12`}
          style={{ ...handleStyle, left: "30%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.Top}
          id={`${nodeId}-source-13`}
          style={{ ...handleStyle, left: "70%", top: "0%" }}
        />
        <Handle
          type="target"
          position={Position.Top}
          id={`${nodeId}-target-14`}
          style={{ ...handleStyle, left: "70%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.Bottom}
          id={`${nodeId}-source-15`}
          style={{ ...handleStyle, left: "30%" }}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id={`${nodeId}-target-15`}
          style={{ ...handleStyle, left: "30%" }}
        />

        <Handle
          type="source"
          position={Position.Bottom}
          id={`${nodeId}-source-16`}
          style={{ ...handleStyle, left: "70%" }}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id={`${nodeId}-target-16`}
          style={{ ...handleStyle, left: "70%" }}
        />
      </>
    );
  };

  // Create a separate component for circular handles
  const CircularHandles = () => {
    return (
      <>
        <Handle
          type="source"
          position={Position.Top}
          id={`${nodeId}-source-0`}
          style={{ ...handleStyle, left: "50%", top: "0%" }}
        />
        <Handle
          type="target"
          position={Position.Top}
          id={`${nodeId}-target`}
          style={{ ...handleStyle, left: "50%", top: "0%" }}
        />

        <Handle
          type="source"
          position={Position.TopRight}
          id={`${nodeId}-source-1`}
          style={{ ...handleStyle, left: "85%", top: "15%" }}
        />
        <Handle
          type="target"
          position={Position.TopRight}
          id={`${nodeId}-target-1`}
          style={{ ...handleStyle, left: "85%", top: "15%" }}
        />

        <Handle
          type="source"
          position={Position.Right}
          id={`${nodeId}-source-2`}
          style={{ ...handleStyle, left: "100%", top: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Right}
          id={`${nodeId}-target-2`}
          style={{ ...handleStyle, left: "100%", top: "50%" }}
        />

        <Handle
          type="source"
          position={Position.BottomRight}
          id={`${nodeId}-source-3`}
          style={{ ...handleStyle, left: "85%", top: "85%" }}
        />
        <Handle
          type="target"
          position={Position.BottomRight}
          id={`${nodeId}-target-3`}
          style={{ ...handleStyle, left: "85%", top: "85%" }}
        />

        <Handle
          type="source"
          position={Position.Bottom}
          id={`${nodeId}-source`}
          style={{ ...handleStyle, left: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id={`${nodeId}-target-4`}
          style={{ ...handleStyle, left: "50%" }}
        />

        <Handle
          type="source"
          position={Position.BottomLeft}
          id={`${nodeId}-source-5`}
          style={{ ...handleStyle, left: "15%", top: "85%" }}
        />
        <Handle
          type="target"
          position={Position.BottomLeft}
          id={`${nodeId}-target-5`}
          style={{ ...handleStyle, left: "15%", top: "85%" }}
        />

        <Handle
          type="source"
          position={Position.Left}
          id={`${nodeId}-source-6`}
          style={{ ...handleStyle, left: "0%", top: "50%" }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id={`${nodeId}-target-6`}
          style={{ ...handleStyle, left: "0%", top: "50%" }}
        />

        <Handle
          type="source"
          position={Position.TopLeft}
          id={`${nodeId}-source-7`}
          style={{ ...handleStyle, left: "15%", top: "15%" }}
        />
        <Handle
          type="target"
          position={Position.TopLeft}
          id={`${nodeId}-target-7`}
          style={{ ...handleStyle, left: "15%", top: "15%" }}
        />
      </>
    );
  };

  const PerimeterHandles = hasChildren ? <RectangularHandles /> : <CircularHandles />;

  return (
    <div
      ref={nodeRef}
      style={nodeStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-floating-node="true"
      className="group"
    >
      {data.label && data.position && (
        <GroupNodeLabel
          label={data.label}
          type={data.type}
          position={hasChildren ? data.position : "center"}
          isCircle={!hasChildren}
        />
      )}

      {!hasChildren ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "0px"
          }}
        >
          <AddButton />
        </div>
      ) : (
        <>
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              display: "flex",
              gap: "6px",
              opacity: hovered || selected ? 1 : 0,
              transition: "opacity 0.2s ease"
            }}
          >
            <AddButton size="small" />
          </div>

          {sides.map(({ position, style }) => (
            <NodeResizeControl
              key={position}
              position={position}
              minWidth={MIN_WIDTH}
              minHeight={MIN_HEIGHT}
              style={{ ...style }}
              onResize={(_, { width, height }) => {
                setDimensions({
                  width: Math.max(width, MIN_WIDTH),
                  height: Math.max(height, MIN_HEIGHT)
                });
                setTimeout(() => updateNodeInternals(id), 200);
              }}
            />
          ))}
        </>
      )}

      {PerimeterHandles}
    </div>
  );
};

export default React.memo(GroupNode);

const sides = [
  { position: "top", style: { top: 0, width: "100%", cursor: "ns-resize" } },
  { position: "bottom", style: { bottom: 0, width: "100%", cursor: "ns-resize" } },
  { position: "left", style: { left: 0, height: "100%", cursor: "ew-resize" } },
  { position: "right", style: { right: 0, height: "100%", cursor: "ew-resize" } },
  {
    position: "top-left",
    style: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "24px",
      height: "24px",
      cursor: "nwse-resize"
    }
  },
  {
    position: "top-right",
    style: {
      position: "absolute",
      top: 0,
      right: 0,
      width: "24px",
      height: "24px",
      cursor: "nesw-resize"
    }
  },
  {
    position: "bottom-left",
    style: {
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "24px",
      height: "24px",
      cursor: "nesw-resize"
    }
  },
  {
    position: "bottom-right",
    style: {
      position: "absolute",
      bottom: 0,
      right: 0,
      width: "24px",
      height: "24px",
      cursor: "nwse-resize"
    }
  }
];
